import { useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  ApiError,
  addCryptoFund,
  addFundByISIN,
  listFunds,
  refreshFund,
  uploadFundPdf,
  type Fund,
} from '../api';

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; name: string }
  | { kind: 'error'; msg: string };

type IsinState = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'error'; msg: string };

export default function Funds() {
  const { accountId = '' } = useParams<{ accountId: string }>();
  const { me } = useAuth();
  const account = me?.accounts.find((a) => a.id === accountId);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upload, setUpload] = useState<UploadState>({ kind: 'idle' });
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const [isin, setIsin] = useState('');
  const [isinState, setIsinState] = useState<IsinState>({ kind: 'idle' });

  const [coinId, setCoinId] = useState('');
  const [coinState, setCoinState] = useState<IsinState>({ kind: 'idle' });

  // Per-fund refresh state keyed by fund id — so two simultaneous refreshes
  // each get their own spinner without stomping on each other.
  const [refreshing, setRefreshing] = useState<Record<string, boolean>>({});
  const [refreshErr, setRefreshErr] = useState<Record<string, string>>({});

  async function load() {
    setError(null);
    try {
      const res = await listFunds(accountId);
      setFunds(res.funds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!accountId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  if (!me) return null;
  if (!account) {
    return <Navigate to="/accounts" replace />;
  }

  async function handleFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setUpload({ kind: 'error', msg: 'Only .pdf files are accepted.' });
      return;
    }
    setUpload({ kind: 'uploading', name: file.name });
    try {
      await uploadFundPdf(accountId, file);
      setUpload({ kind: 'idle' });
      await load();
    } catch (err) {
      setUpload({ kind: 'error', msg: asMessage(err, 'Upload failed') });
    }
  }

  async function onAddByIsin(e: FormEvent) {
    e.preventDefault();
    const trimmed = isin.trim().toUpperCase();
    if (!trimmed) return;
    setIsinState({ kind: 'submitting' });
    try {
      await addFundByISIN(accountId, trimmed);
      setIsin('');
      setIsinState({ kind: 'idle' });
      await load();
    } catch (err) {
      setIsinState({ kind: 'error', msg: asMessage(err, 'Failed to add fund') });
    }
  }

  async function onAddCoin(e: FormEvent) {
    e.preventDefault();
    const trimmed = coinId.trim().toLowerCase();
    if (!trimmed) return;
    setCoinState({ kind: 'submitting' });
    try {
      await addCryptoFund(accountId, trimmed);
      setCoinId('');
      setCoinState({ kind: 'idle' });
      await load();
    } catch (err) {
      setCoinState({ kind: 'error', msg: asMessage(err, 'Failed to add coin') });
    }
  }

  async function onRefresh(fundId: string) {
    setRefreshing((r) => ({ ...r, [fundId]: true }));
    setRefreshErr((e) => withoutKey(e, fundId));
    try {
      await refreshFund(accountId, fundId);
      await load();
    } catch (err) {
      setRefreshErr((e) => ({ ...e, [fundId]: asMessage(err, 'Refresh failed') }));
    } finally {
      setRefreshing((r) => withoutKey(r, fundId));
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  if (loading) return <p>Loading…</p>;

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        <Link to="/accounts">← households</Link>
      </p>
      <h2 style={{ marginBottom: '0.25rem' }}>Funds</h2>
      <p style={{ color: '#666', margin: '0 0 1rem' }}>{account.name}</p>
      {error && <p style={{ color: '#b00' }}>{error}</p>}

      <form
        onSubmit={onAddByIsin}
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <input
          value={isin}
          onChange={(e) => setIsin(e.target.value)}
          placeholder="Add by ISIN — e.g. GB00B3TYHH97"
          style={{ flex: 1, padding: '0.5rem 0.75rem' }}
          disabled={isinState.kind === 'submitting'}
        />
        <button type="submit" disabled={isinState.kind === 'submitting' || !isin.trim()}>
          {isinState.kind === 'submitting' ? 'Adding…' : 'Add fund'}
        </button>
      </form>
      {isinState.kind === 'error' && (
        <p style={{ color: '#b00', margin: '0 0 1rem' }}>{isinState.msg}</p>
      )}

      <form
        onSubmit={onAddCoin}
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <input
          value={coinId}
          onChange={(e) => setCoinId(e.target.value)}
          placeholder="Add crypto by CoinGecko id — e.g. bitcoin, ethereum, solana"
          style={{ flex: 1, padding: '0.5rem 0.75rem' }}
          disabled={coinState.kind === 'submitting'}
        />
        <button type="submit" disabled={coinState.kind === 'submitting' || !coinId.trim()}>
          {coinState.kind === 'submitting' ? 'Adding…' : 'Add coin'}
        </button>
      </form>
      {coinState.kind === 'error' && (
        <p style={{ color: '#b00', margin: '0 0 1rem' }}>{coinState.msg}</p>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#3b82f6' : '#bbb'}`,
          background: dragOver ? '#eff6ff' : '#fafafa',
          borderRadius: 8,
          padding: '1.25rem',
          textAlign: 'center',
          cursor: 'pointer',
          marginBottom: '1.5rem',
        }}
      >
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = '';
          }}
        />
        {upload.kind === 'uploading' ? (
          <p style={{ margin: 0 }}>Uploading {upload.name}…</p>
        ) : (
          <>
            <p style={{ margin: 0, fontWeight: 500 }}>…or drop a Morningstar PDF here</p>
            <p style={{ margin: '0.25rem 0 0', color: '#666', fontSize: '0.9rem' }}>
              Adds the same fields as ISIN add, plus the allocation breakdown the PDF carries.
            </p>
          </>
        )}
        {upload.kind === 'error' && (
          <p style={{ margin: '0.5rem 0 0', color: '#b00' }}>{upload.msg}</p>
        )}
      </div>

      {funds.length === 0 ? (
        <p style={{ color: '#666' }}>No funds yet — add one by ISIN or upload a PDF.</p>
      ) : (
        <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
          {funds.map((f) => (
            <li
              key={f.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: 6,
                padding: '0.75rem 1rem',
                marginBottom: '0.5rem',
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <div>
                  <Link
                    to={`/accounts/${encodeURIComponent(accountId)}/funds/${encodeURIComponent(f.id)}`}
                    style={{ color: 'inherit', fontWeight: 500 }}
                  >
                    {f.name || f.id}
                  </Link>
                  <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.15rem' }}>
                    {f.category}
                    {f.sedol && <span> · SEDOL {f.sedol}</span>}
                    {f.isin && <span> · ISIN {f.isin}</span>}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    color: '#888',
                    fontSize: '0.85rem',
                  }}
                >
                  {f.ongoing_charge_pct > 0 && <span>OCF {f.ongoing_charge_pct.toFixed(2)}%</span>}
                  <button
                    onClick={() => void onRefresh(f.id)}
                    disabled={refreshing[f.id]}
                    title="Re-fetch latest NAV from Morningstar"
                  >
                    {refreshing[f.id] ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
              </div>
              {refreshErr[f.id] && (
                <p style={{ color: '#b00', margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
                  {refreshErr[f.id]}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function asMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

function withoutKey<V>(obj: Record<string, V>, key: string): Record<string, V> {
  if (!(key in obj)) return obj;
  const out: Record<string, V> = {};
  for (const k of Object.keys(obj)) {
    if (k !== key) out[k] = obj[k];
  }
  return out;
}
